import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Input, TextArea } from 'ui-kit';
import { BUTTON_TYPES } from '_constants';
import * as Styled from './notes.styles';

// RetailerNote.reason is a CharField(max_length=255).
const REASON_MAX = 255;

/**
 * The add / edit form for a manual note: an optional reason and the (required) text.
 *
 * `onSubmit` receives the trimmed fields and resolves to true when the save went through. The parent
 * closes (unmounts) the form then; a failed request leaves it open with what the user typed.
 */
const NoteEditor = ({ initialReason, initialText, submitLabel, onSubmit, onCancel, idPrefix }) => {
  const [reason, setReason] = useState(initialReason);
  const [text, setText] = useState(initialText);
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);

  const trimmedText = text.trim();
  const textError = touched && !trimmedText ? 'Note text is required' : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (!trimmedText || saving) return;
    setSaving(true);
    const saved = await onSubmit({ reason: reason.trim(), text: trimmedText });
    // On success the form is already gone — no state to touch.
    if (!saved) setSaving(false);
  };

  return (
    <Styled.Editor onSubmit={handleSubmit} noValidate>
      <Input
        name={`${idPrefix}-reason`}
        title="Reason"
        placeholder="e.g. Phone call with owner"
        value={reason}
        maxLength={REASON_MAX}
        onChange={(e) => setReason(e.target.value)}
        disabled={saving}
      />
      <TextArea
        name={`${idPrefix}-text`}
        title="Note"
        required
        value={text}
        error={textError}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => setTouched(true)}
        disabled={saving}
        autoFocus
      />
      <Styled.EditorActions>
        <Button variant={BUTTON_TYPES.OUTLINE} text="Cancel" onClick={onCancel} disabled={saving} />
        <Button variant={BUTTON_TYPES.PRIMARY} type="submit" text={submitLabel} loading={saving} disabled={saving} />
      </Styled.EditorActions>
    </Styled.Editor>
  );
};

NoteEditor.propTypes = {
  initialReason: PropTypes.string,
  initialText: PropTypes.string,
  submitLabel: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  // Keeps the field ids unique when an edit form and the add form are open at once.
  idPrefix: PropTypes.string.isRequired,
};

NoteEditor.defaultProps = {
  initialReason: '',
  initialText: '',
  submitLabel: 'Save',
};

export default NoteEditor;
